/** Human-readable Russian labels for dimensions and statuses (CHC-869). */

import type { ConfigStatus } from './model/coverage'
import type { RoutingContext, TrafficType, Source, Direction } from './model/types'

export const STATUS_LABEL: Record<ConfigStatus, string> = {
  CONFIGURED: 'Настроено',
  // CHC-874: «Пусто» убран как отдельный смысл. Конфиг без рабочего адаптера
  // не маршрутизирует НЕПРЕДНАМЕРЕННО — это та же «нужно настроить», что и
  // пропуск, а не нейтральное «пусто». Поэтому EMPTY читается как риск.
  EMPTY: 'Требует настройки',
  INCOMPLETE: 'Неполная',
  CONFLICT: 'Конфликт',
  DISABLED: 'Выключена',
}

/** Bootstrap badge color suffix per status (badge-light-*). */
export const STATUS_BADGE: Record<ConfigStatus, string> = {
  CONFIGURED: 'success',
  EMPTY: 'danger', // CHC-874: риск, а не нейтральный secondary
  INCOMPLETE: 'warning',
  CONFLICT: 'danger',
  DISABLED: 'dark',
}

export const DIRECTION_LABEL: Record<Direction, string> = {
  PAYMENT: 'Приём',
  PAYOUT: 'Выплата',
}

export const SOURCE_LABEL: Record<Source, string> = {
  MERCHANT: 'Мерчант',
  PLATFORM: 'Платформа',
}

export const TRAFFIC_LABEL: Record<TrafficType, string> = {
  NONE: 'Любой трафик',
  FIRST: 'Только первичный',
  SECOND: 'Только вторичный',
}

export const TRAFFIC_OPTIONS = [
  { value: 'NONE', label: TRAFFIC_LABEL.NONE },
  { value: 'FIRST', label: TRAFFIC_LABEL.FIRST },
  { value: 'SECOND', label: TRAFFIC_LABEL.SECOND },
]

export const SOURCE_OPTIONS = [
  { value: 'MERCHANT', label: 'MERCHANT' },
  { value: 'PLATFORM', label: 'PLATFORM' },
]

export const METHOD_LABEL: Record<string, string> = {
  CARD: 'Карта (P2P)',
  SBP: 'СБП',
}

export const FLAG_LABEL: Record<string, string> = {
  // CHC-892: реальные значения externalUserFlag — это ЦВЕТА. ALL/NEW/RETURNING
  // в проде не встречаются, но оставлены на всякий случай.
  ALL: 'Все',
  NEW: 'Новые',
  RETURNING: 'Вернувшиеся',
  white: 'Белый',
  red: 'Красный',
  yellow: 'Жёлтый',
  green: 'Зелёный',
  blue: 'Синий',
  orange: 'Оранжевый',
  black: 'Чёрный',
  purple: 'Фиолетовый',
}

/**
 * CHC-892: цвет дота для цветового клиентского флага. Неизвестный флаг → серый.
 * white получает тонкую рамку отдельно (иначе невидим на светлом фоне).
 */
export const FLAG_COLOR: Record<string, string> = {
  white: '#ffffff',
  red: '#f1416c',
  yellow: '#ffc700',
  green: '#50cd89',
  blue: '#009ef7',
  orange: '#fd7e14',
  black: '#000000',
  purple: '#7239ea',
}

export function flagColor(flag: string): string | null {
  return FLAG_COLOR[flag] ?? null
}

export function methodLabel(type: string): string {
  return METHOD_LABEL[type] ?? type
}

export function flagLabel(flag: string): string {
  return FLAG_LABEL[flag] ?? flag
}

/**
 * Compact one-line label of a context's non-merchant dimensions.
 *
 * CHC-877: клиентский флаг (externalUserFlag) убран отсюда — он рендерится
 * отдельным наглядным чипом ({@link flagLabel} + FlagChip), а не текстом в строке.
 */
export function contextLabel(ctx: RoutingContext): string {
  return [
    methodLabel(ctx.paymentMethodType),
    DIRECTION_LABEL[ctx.direction],
    ctx.currency,
    SOURCE_LABEL[ctx.source],
  ].join(' · ')
}

export function formatRub(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—'
  return `${n.toLocaleString('ru-RU')} ₽`
}

export function formatUpdatedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Russian text of the routing editor's refusals, by errorCode. */
export const ROUTING_ERROR_TEXT: Record<string, string> = {
  PAYMENT_ADAPTER_PRIORITY_NOT_UNIQUE: 'Приоритеты адаптеров не должны повторяться',
  MERCHANT_NOT_FOUND: 'Мерчант не найден',
  PAYMENT_METHOD_UNKNOWN: 'Неизвестный платёжный метод',
}
